import {
  getObject,
  getList,
  LeoUUID,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class AssignEmailCycleRPC implements
  LeoRPC<AssignEmailCycleRPC.Request, AssignEmailCycleRPC.Response, AssignEmailCycleRPC.Errors.Errors>
{
  abstract execute(
    request: AssignEmailCycleRPC.Request
  ): Promise<LeoRPCResult<AssignEmailCycleRPC.Response, AssignEmailCycleRPC.Errors.Errors>>;
}

export namespace AssignEmailCycleRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    respondents: RespondentSelection.RespondentSelectionDTO;
    scheduleIds: string[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAssignEmailCycleRPCRequest: boolean = true;
  
    readonly respondents: RespondentSelection.RespondentSelection;
    readonly scheduleIds: LeoUUID[];
  
  
    constructor(
    respondents: RespondentSelection.RespondentSelection, 
    scheduleIds: LeoUUID[]
    ) {
      this.respondents = respondents;
      this.scheduleIds = scheduleIds;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const respondents = RespondentSelection.fromDTO(getObject(dto, "respondents", InvalidRequestRespondentsError));
      const scheduleIds = getList<string, LeoUUID>(
        dto,
        "scheduleIds",
        (e: string) => new LeoUUID(e),
        InvalidRequestScheduleIdsError
      );
      return new Request(
        respondents,
        scheduleIds
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          respondents: this.respondents.toDTO(),
          scheduleIds: this.scheduleIds.map((e: LeoUUID) => { return e.toDTO(); })
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `AssignEmailCycleRPC(respondents='${this.respondents}',scheduleIds='${this.scheduleIds}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `AssignEmailCycleRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidScheduleId = "INVALID_SCHEDULE_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    EmailCycleInactive = "EMAIL_CYCLE_INACTIVE",
    PendingSurveyAssignment = "PENDING_SURVEY_ASSIGNMENT",
    EmailCycleInProgress = "EMAIL_CYCLE_IN_PROGRESS",
    MissingEmailId = "MISSING_EMAIL_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AssignEmailCycleRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidScheduleIdDTO {
      code: string;
    }
    
    export class InvalidScheduleId extends LeoRPCError<RPCError> {
      readonly isInvalidScheduleId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidScheduleId;
        super(code);
      }
    
    
      toDTO(): InvalidScheduleIdDTO {
        const dto: InvalidScheduleIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidScheduleId.prototype.toString = function toString(): string {
      return `AssignEmailCycleRPC.INVALID_SCHEDULE_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `AssignEmailCycleRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface EmailCycleInactiveDTO {
      code: string;
    }
    
    export class EmailCycleInactive extends LeoRPCError<RPCError> {
      readonly isEmailCycleInactive: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.EmailCycleInactive;
        super(code);
      }
    
    
      toDTO(): EmailCycleInactiveDTO {
        const dto: EmailCycleInactiveDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    EmailCycleInactive.prototype.toString = function toString(): string {
      return `AssignEmailCycleRPC.EMAIL_CYCLE_INACTIVE()`;
    };
    
    
    export interface PendingSurveyAssignmentDTO {
      code: string;
      respondentsSelected: number;
      respondentsAssigned: number;
      respondentsUnassigned: number;
    }
    
    export class PendingSurveyAssignment extends LeoRPCError<RPCError> {
      readonly isPendingSurveyAssignment: boolean = true;
    
      readonly respondentsSelected: number;
      readonly respondentsAssigned: number;
      readonly respondentsUnassigned: number;
    
      constructor(
      respondentsSelected: number, 
      respondentsAssigned: number, 
      respondentsUnassigned: number
      ) {
        const code = RPCError.PendingSurveyAssignment;
        super(code);
        this.respondentsSelected = respondentsSelected;
        this.respondentsAssigned = respondentsAssigned;
        this.respondentsUnassigned = respondentsUnassigned;
      }
    
      static fromDTO(dto: PendingSurveyAssignmentDTO): PendingSurveyAssignment {
        const respondentsSelected = getInt32(dto, "respondentsSelected", InvalidResponsePendingSurveyAssignmentRespondentsSelectedError);
        const respondentsAssigned = getInt32(dto, "respondentsAssigned", InvalidResponsePendingSurveyAssignmentRespondentsAssignedError);
        const respondentsUnassigned = getInt32(dto, "respondentsUnassigned", InvalidResponsePendingSurveyAssignmentRespondentsUnassignedError);
        return new PendingSurveyAssignment(
          respondentsSelected,
          respondentsAssigned,
          respondentsUnassigned
        );
      }
    
      toDTO(): PendingSurveyAssignmentDTO {
        const dto: PendingSurveyAssignmentDTO = {
          code: this.code,
          respondentsSelected: this.respondentsSelected,
          respondentsAssigned: this.respondentsAssigned,
          respondentsUnassigned: this.respondentsUnassigned
        };
        return dto;
      }
    
      }
    
    PendingSurveyAssignment.prototype.toString = function toString(): string {
      return `AssignEmailCycleRPC.PENDING_SURVEY_ASSIGNMENT(respondentsSelected='${this.respondentsSelected}',respondentsAssigned='${this.respondentsAssigned}',respondentsUnassigned='${this.respondentsUnassigned}')`;
    };
    
    
    export interface EmailCycleInProgressDTO {
      code: string;
    }
    
    export class EmailCycleInProgress extends LeoRPCError<RPCError> {
      readonly isEmailCycleInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.EmailCycleInProgress;
        super(code);
      }
    
    
      toDTO(): EmailCycleInProgressDTO {
        const dto: EmailCycleInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    EmailCycleInProgress.prototype.toString = function toString(): string {
      return `AssignEmailCycleRPC.EMAIL_CYCLE_IN_PROGRESS()`;
    };
    
    
    export interface MissingEmailIdDTO {
      code: string;
      count: number;
    }
    
    export class MissingEmailId extends LeoRPCError<RPCError> {
      readonly isMissingEmailId: boolean = true;
    
      readonly count: number;
    
      constructor(
      count: number
      ) {
        const code = RPCError.MissingEmailId;
        super(code);
        this.count = count;
      }
    
      static fromDTO(dto: MissingEmailIdDTO): MissingEmailId {
        const count = getInt32(dto, "count", InvalidResponseMissingEmailIdCountError);
        return new MissingEmailId(
          count
        );
      }
    
      toDTO(): MissingEmailIdDTO {
        const dto: MissingEmailIdDTO = {
          code: this.code,
          count: this.count
        };
        return dto;
      }
    
      }
    
    MissingEmailId.prototype.toString = function toString(): string {
      return `AssignEmailCycleRPC.MISSING_EMAIL_ID(count='${this.count}')`;
    };
    

    export type Errors = InvalidProjectId | InvalidScheduleId | ProjectAlreadyArchived | EmailCycleInactive | PendingSurveyAssignment | EmailCycleInProgress | MissingEmailId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidScheduleIdDTO | ProjectAlreadyArchivedDTO | EmailCycleInactiveDTO | PendingSurveyAssignmentDTO | EmailCycleInProgressDTO | MissingEmailIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidScheduleId:
          return new InvalidScheduleId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.EmailCycleInactive:
          return new EmailCycleInactive();
        case RPCError.PendingSurveyAssignment:
          return PendingSurveyAssignment.fromDTO(dto as PendingSurveyAssignmentDTO);
        case RPCError.EmailCycleInProgress:
          return new EmailCycleInProgress();
        case RPCError.MissingEmailId:
          return MissingEmailId.fromDTO(dto as MissingEmailIdDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestRespondentsError extends AssignEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentsError";
  }
}

export class InvalidRequestScheduleIdsError extends AssignEmailCycleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScheduleIdsError";
  }
}


export class InvalidResponseInvalidProjectIdError extends AssignEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidScheduleIdError extends AssignEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidScheduleIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends AssignEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseEmailCycleInactiveError extends AssignEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmailCycleInactiveError";
  }
}

export class InvalidResponsePendingSurveyAssignmentError extends AssignEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentError";
  }
}

export class InvalidResponsePendingSurveyAssignmentRespondentsSelectedError extends InvalidResponsePendingSurveyAssignmentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentRespondentsSelectedError";
  }
}

export class InvalidResponsePendingSurveyAssignmentRespondentsAssignedError extends InvalidResponsePendingSurveyAssignmentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentRespondentsAssignedError";
  }
}

export class InvalidResponsePendingSurveyAssignmentRespondentsUnassignedError extends InvalidResponsePendingSurveyAssignmentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePendingSurveyAssignmentRespondentsUnassignedError";
  }
}
export class InvalidResponseEmailCycleInProgressError extends AssignEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmailCycleInProgressError";
  }
}

export class InvalidResponseMissingEmailIdError extends AssignEmailCycleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMissingEmailIdError";
  }
}

export class InvalidResponseMissingEmailIdCountError extends InvalidResponseMissingEmailIdError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMissingEmailIdCountError";
  }
}
